import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassRegister } from '../../shared/class-register.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { Pupil } from '../../shared/pupil.model';
import { SchoolClass } from '../../shared/school-class.model';
import { SickNote } from '../../shared/sick-note.entity';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { TableRowEvent } from 'src/modules/sm-base/models/table-row-event.model';
import { TableCellButton } from 'src/modules/sm-base/models/table-cell-button.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { Objects } from 'src/modules/utils/shared/objects';

@Component({
    selector: 'app-sick-notes',
    templateUrl: './sick-notes.component.html',
    styleUrls: ['./sick-notes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SickNotesComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { id: "number?", year: "number?", isHalfYear: "bool?" };
    id: number;
    year: number;
    isHalfYear: boolean;

    isAdmin = false;
    enaioSchoolClass: SchoolClass;
    classRegister: ClassRegister;
    pupils: Pupil[];
    sickNotes: SickNote[];
    table: TableData;
    form: FrontendForm;

    async initParams(): Promise<boolean> {
        if (!this.isGlobal()) {
            this.classRegister = await this.service.restGetClassRegister(this.id);
            this.enaioSchoolClass = await this.service.restGetSchoolClass(this.classRegister.schoolClassId);
            this.year = this.enaioSchoolClass.getYearNumber();
            this.isHalfYear = this.classRegister.isHalfYear;
            await this.loadSickNotes();
        }

        this.isAdmin = this.service.isSecretary() || this.enaioSchoolClass.hasPermission(this.app.getUserName(), true);

        this.pupils = !this.isGlobal() ? await this.service.restGetPupils(this.enaioSchoolClass.id) : await this.service.restGetPupilsForYear(EnaioCertificatesTools.getSchoolYearFromYear(this.year));

        this.createForm();
        this.updateTable();

        this.app.updateNavigation("Abmeldungen", [
            this.service.getHomeClassRegister(),
            this.id == null ? { label: "Sekretariat", routerLink: ["/enaio-certificates/class-registers/home"] } : { label: "Klasse " + this.enaioSchoolClass.getDisplayName(), routerLink: ["/enaio-certificates", "class-registers", "class-register", this.classRegister.id] },
            { label: "Abmeldungen", routerLink: this.id == null ? ["/enaio-certificates", "class-registers", "sick-notes", this.year, this.isHalfYear ? 1 : 0] : ["/enaio-certificates", "class-registers", "sick-notes-class", this.id]}
        ]);
        return true;
    }

    isGlobal(): boolean {
        return this.id == null;
    }

    async loadSickNotes(): Promise<void> {
        this.sickNotes = await RestEndpoint.main().query({ids: this.id}).run("api/cer/classregister/sicknote").list(SickNote);
    }

    async addSickNote(): Promise<void> {
        if (!this.app.validateForm(this.form, () => !Utils.isNumber(this.form.getValue("pupilId")) ? [new FrontendFieldError("pupilId", "Der Schüler ist unbekannt")] : [])) {
            return;
        }

        let note = new SickNote();
        this.form.get(note);
        if (this.isGlobal()) {
            note.excused = true;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().post().query(Objects.removeNullValues({
            isHalfYear: this.isHalfYear,
            classRegisterId: this.isGlobal() ? null : this.classRegister.id
        })).body(note).run("api/cer/classregister/sicknote").getText(), this, true, false, "Abgelegt", "Die Abmeldung wurde abgelegt");
        if (ok) {
            if (!this.isGlobal()) {
                this.sickNotes = [...this.sickNotes, note];
            }
            this.updateTable();
            this.createForm();
        }
    }

    async deleteSickNote(event: TableRowEvent): Promise<void> {
        if ((await this.app.deleteDataHandler(async () => RestEndpoint.main().delete().query({id: (event.row.raw as SickNote).id}).run("api/cer/classregister/sicknote").getText()))[0]) {
            await this.loadSickNotes();
            this.updateTable();
        }
    }

    async sickNoteClicked(event: TableCellEvent): Promise<void> {
        if (event.isButton("edit", "edit")) {
            let sn = event.row.raw as SickNote;
            let form = this.service.getSickNoteForm(this.pupils, true, false);
            form.fields = form.fields.filter(f => f.id != "pupilId"); //TODO Aktuell notwendig, weil AutoComplete Dropdown nicht richtig vorbelegt wird.
            form.fill(sn);
            if (await this.app.dialog.form(form, "Abmeldung bearbeiten") == "ok") {
                let snNew = new SickNote();
                form.get(snNew);
                snNew.id = sn.id;
                snNew.pupilId = sn.pupilId;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().put().query(Objects.removeNullValues({
                    isHalfYear: this.isHalfYear,
                    classRegisterId: this.isGlobal() ? null : this.classRegister.id
                })).body(snNew).run("api/cer/classregister/sicknote").getText(), this, true, false);
                if (ok) {
                    form.get(sn);
                    if (!this.isGlobal) {
                        this.sickNotes = [...this.sickNotes];
                        this.updateTable();
                    }
                }
            }
        }
    }

    private updateTable(): void {
        this.table = !this.isGlobal() ? new TableData([
            new TableColumn("pupil", "Schüler", TableCellType.string, 30),
            new TableColumn("fromDate", "Von", TableCellType.date, 1),
            new TableColumn("toDate", "Bis", TableCellType.date, 1),
            new TableColumn("fromHour", "Von Stunde", TableCellType.string, 2),
            new TableColumn("toHour", "Bis Stunde", TableCellType.string, 2),
            new TableColumn("reason", "Grund der Abmeldung", TableCellType.string, 30),
            new TableColumn("excused", "Entschuldigt", TableCellType.bool, 1),
            new TableColumn("createdBy", "Angelegt von", TableCellType.string, 20),
            this.isAdmin ? new TableColumn("edit", "", TableCellType.button, 1) : null
        ], this.sickNotes.map(sn => new TableRow("", sn, {
            pupil: Arrays.findAndConvert(this.pupils, p => p.id == sn.pupilId, p => p.getFullName(), "<Ungültiger Schüler>"),
            fromDate: sn.fromDate,
            toDate: sn.toDate,
            fromHour: sn.fromHour == -1 ? "-" : sn.fromHour + 1,
            toHour: sn.toHour == -1 ? "-" : sn.toHour + 1,
            reason: sn.reason,
            excused: sn.excused,
            createdBy: sn.createdBy.includes("@") ? sn.createdBy : "Schule",
            edit: [new TableCellButton("edit", "fas fa-pencil", "Bearbeiten")]
        })), [new TableSortColumn("fromDate", false)]) : null;
    }

    private createForm(): void {
        this.form = this.service.getSickNoteForm(this.pupils, !this.isGlobal(), false);
    }

}
